import { Link } from 'react-router-dom';
import { AlertTriangle, AlertOctagon, Info, ChevronRight } from 'lucide-react';
import type { OperationalAlert } from '@/types/dashboard';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';

const SEVERITY_STYLES: Record<OperationalAlert['severity'], { icon: typeof Info; tone: string }> = {
  critical: { icon: AlertOctagon, tone: 'text-destructive' },
  warning: { icon: AlertTriangle, tone: 'text-warning [color:hsl(38_85%_30%)]' },
  info: { icon: Info, tone: 'text-info' },
};

interface Props {
  alerts: OperationalAlert[];
}

export function AlertsList({ alerts }: Props) {
  if (alerts.length === 0) {
    return (
      <div className="py-8 text-center text-sm text-muted-foreground">
        All clear — no active alerts.
      </div>
    );
  }

  return (
    <ul className="-mx-1 divide-y divide-border/70">
      {alerts.map((alert) => {
        const { icon: Icon, tone } = SEVERITY_STYLES[alert.severity];
        const inner = (
          <div className="flex items-start gap-3 px-1 py-3 transition-colors hover:bg-muted/40">
            <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', tone)} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-sm font-medium text-foreground">{alert.title}</div>
                <div className="shrink-0 text-[11px] text-muted-foreground">
                  {fmt.relative(alert.raisedAt)}
                </div>
              </div>
              {alert.detail && (
                <p className="mt-0.5 text-xs text-muted-foreground">{alert.detail}</p>
              )}
            </div>
            {alert.link && <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />}
          </div>
        );
        return (
          <li key={alert.id}>
            {alert.link ? (
              <Link to={alert.link} className="block rounded-md">
                {inner}
              </Link>
            ) : (
              inner
            )}
          </li>
        );
      })}
    </ul>
  );
}
